import { useState, useEffect } from "react";
import apiService from "../../../../service/Api";
import { ICoverTemplate } from "../types/icover-template";

type PaginatedResponse<T> = {
  count: number;
  results: T[];
  next: string | null;
  previous: string | null;
};

export const useCoverTemplate = () => {
  const [coverTemplates, setCoverTemplates] = useState<ICoverTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCoverTemplates = async () => {
    try {
      const response = await apiService.get<
        PaginatedResponse<ICoverTemplate> | ICoverTemplate[]
      >("/api/configurations/proposal-templates/");

      let data: ICoverTemplate[] = [];

      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (
        Array.isArray(
          (response.data as PaginatedResponse<ICoverTemplate>).results
        )
      ) {
        data = (response.data as PaginatedResponse<ICoverTemplate>).results;
      }

      setCoverTemplates(data);
    } catch (err: unknown) {
      console.error("Failed to fetch cover templates", err);
      setError("Failed to load cover templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverTemplates();
  }, []);

  const addCoverTemplate = async (form: FormData) => {
    try {
      const response = await apiService.post<ICoverTemplate>(
        "/api/configurations/proposal-templates/",
        form
      );
      setCoverTemplates((prev) => [...prev, response.data]);
      return response.data;
    } catch (err: unknown) {
      console.error("Failed to add cover template", err);
      throw err;
    }
  };

  const updateCoverTemplate = async (id: string, form: FormData) => {
    try {
      const response = await apiService.put<ICoverTemplate>(
        `/api/configurations/proposal-templates/${id}/`,
        form
      );
      setCoverTemplates((prev) =>
        prev.map((t) => (t.id === id ? response.data : t))
      );
      return response.data;
    } catch (err: unknown) {
      console.error("Failed to update cover template", err);
      throw err;
    }
  };

  const deleteCoverTemplate = async (id: string) => {
    try {
      await apiService.delete(`/api/configurations/proposal-templates/${id}/`);
      setCoverTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err: unknown) {
      console.error("Failed to delete cover template", err);
      throw err;
    }
  };

  const getActiveCoverTemplates = () => {
    return coverTemplates.filter((t) => t.is_active);
  };

  return {
    coverTemplates,
    loading,
    error,
    addCoverTemplate,
    updateCoverTemplate,
    deleteCoverTemplate,
    getActiveCoverTemplates,
  };
};
